/**
 * Teams / Check-in page search bar. Uses the reusable SearchBar and binds
 * it to parent-provided props (parent can wire Redux/tools or local state).
 * Filter content (file types) is passed as children of the search bar.
 */
import React from "react";
import { ContextualMenu, Stack } from "@fluentui/react";
import { useSearchPanel } from "../../../theme/styles/SearchPanelStyle";
import { useTheme } from "../../../theme/OAS365Theme";
import { ONEDRIVE_FILE_TYPES } from "../../../redux/constants/metaData";
import wordIcon from "../../../assets/word.svg";
import excel from "../../../assets/excel.svg";
import pdf from "../../../assets/icons/pdf.png";
import powerpoint from "../../../assets/powerpoint.svg";
import checkInIcon from "../../../assets/icoCheckin.svg";
import folderUploadIcon from "../../../assets/folderUpload.svg";
import SearchBar from "../../../components/common/SearchBar";

const TeamsSearchBar = ({
  searchValue,
  onSearchValueChange,
  onClearAndSearch,
  selectedFileType,
  onFileTypeChange,
  isFilterVisible,
  onFilterVisibleChange,
  onSearch,
  searchLoading = false,
  placeholder = "Search for documents",
  searchBarRef,
  onBrowseFiles,
  onBrowseFolder,
  browseDisabled = false,
}) => {
  const searchPanelStyle = useSearchPanel();
  const theme = useTheme();
  const [browseMenuVisible, setBrowseMenuVisible] = React.useState(false);
  const browseButtonRef = React.useRef(null);

  const openBrowseMenu = () => {
    if (browseDisabled) return;
    setBrowseMenuVisible((prev) => !prev);
  };

  const filterButton = (fileType, label, icon) => {
    const isSelected = selectedFileType === fileType;
    const disabled = searchLoading;
    const containerStyle = {
      ...(isSelected
        ? searchPanelStyle.FileTypeButtonContainerSelectedStyle
        : searchPanelStyle.FileTypeButtonContainerStyle),
      ...(disabled && { pointerEvents: "none", opacity: 0.6 }),
    };
    return (
      <div
        key={fileType ?? "all"}
        style={containerStyle}
        onClick={() => !disabled && onFileTypeChange?.(fileType)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => !disabled && e.key === "Enter" && onFileTypeChange?.(fileType)}
        aria-disabled={disabled}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onFileTypeChange?.(fileType)}
          style={searchPanelStyle.FileTypeButtonStyle}
        >
          {icon}
          <span style={searchPanelStyle.FileTypeTextStyle}>{label}</span>
        </button>
      </div>
    );
  };

  const filterContent = (
    <>
      {filterButton(ONEDRIVE_FILE_TYPES.allFiles, "All Files", null)}
      {filterButton(
        ONEDRIVE_FILE_TYPES.docx,
        "Word",
        <img src={wordIcon} alt="Word" style={searchPanelStyle.IconImageStyle} />,
      )}
      {filterButton(
        ONEDRIVE_FILE_TYPES.xlsx,
        "Excel",
        <img src={excel} alt="Excel" style={searchPanelStyle.IconImageStyle} />,
      )}
      {filterButton(
        ONEDRIVE_FILE_TYPES.pptx,
        "PowerPoint",
        <img src={powerpoint} alt="PowerPoint" style={searchPanelStyle.IconImageStyle} />,
      )}
      {filterButton(
        ONEDRIVE_FILE_TYPES.pdf,
        "PDF",
        <img src={pdf} alt="PDF" style={searchPanelStyle.PdfFilterIconImageStyle} />,
      )}
    </>
  );

  const browseButton = (
    <div
      ref={browseButtonRef}
      style={{
        ...searchPanelStyle.FilterButtonContainerStyle,
        width: "auto",
        minWidth: "auto",
        maxWidth: "none",
        padding: "0 16px",
        backgroundColor: theme.colors.addovationMainBtnColor,
        border: `1px solid ${theme.colors.addovationMainBtnColor}`,
        ...(browseDisabled && { pointerEvents: "none", opacity: 0.6 }),
      }}
      onClick={openBrowseMenu}
      role="button"
      tabIndex={browseDisabled ? -1 : 0}
      aria-disabled={browseDisabled}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openBrowseMenu();
        }
      }}
    >
      <button
        type="button"
        disabled={browseDisabled}
        style={{
          ...searchPanelStyle.FilterButtonStyle,
          backgroundColor: "transparent",
          color: "#ffffff",
          fontSize: 13,
          fontFamily: theme.fonts.regular,
          fontWeight: 500,
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          openBrowseMenu();
        }}
      >
        Browse
        {browseMenuVisible && <span style={{ marginLeft: 4, fontSize: 10, lineHeight: 1 }}>▼</span>}
      </button>
    </div>
  );

  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { width: "100%", flexWrap: "wrap" } }}>
      <div style={{ flex: 1, minWidth: 280 }}>
        <SearchBar
          ref={searchBarRef}
          value={searchValue}
          onChange={onSearchValueChange}
          onSearch={onSearch}
          onClearAndSearch={onClearAndSearch}
          placeholder={placeholder}
          disabled={false}
          loading={searchLoading}
          showFilterButton
          isFilterVisible={isFilterVisible}
          onFilterVisibleChange={onFilterVisibleChange}
          filterContent={filterContent}
          afterFilterContent={browseButton}
        />
      </div>
      <ContextualMenu
        items={[
          {
            key: "browse-files",
            text: "File(s)",
            iconProps: {
              imageProps: {
                src: checkInIcon,
                width: 20,
                height: 20,
                alt: "File(s)",
              },
            },
            onClick: () => {
              setBrowseMenuVisible(false);
              onBrowseFiles?.();
            },
          },
          {
            key: "browse-folder",
            text: "Folder",
            iconProps: {
              imageProps: {
                src: folderUploadIcon,
                width: 18,
                height: 18,
                alt: "Folder",
              },
            },
            onClick: () => {
              setBrowseMenuVisible(false);
              onBrowseFolder?.();
            },
          },
        ]}
        target={browseButtonRef.current}
        onDismiss={() => setBrowseMenuVisible(false)}
        hidden={!browseMenuVisible}
        shouldFocusOnMount
        styles={{
          root: {
            backgroundColor: theme.colors.contextMenuBackground,
            selectors: {
              ".ms-ContextualMenu-link:hover": {
                backgroundColor: theme.colors.contextMenuHover,
              },
              ".ms-ContextualMenu-itemText": {
                fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts.regular,
                fontSize: theme.contextMenu?.fontSize ?? theme.fontSizes?.menusize ?? 12,
                color: theme.colors.fontColor,
              },
              ".ms-ContextualMenu-linkContent": {
                fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts.regular,
                fontSize: theme.contextMenu?.fontSize ?? theme.fontSizes?.menusize ?? 12,
              },
            },
          },
        }}
      />
    </Stack>
  );
};

export default TeamsSearchBar;
